import type {
  HeroAnimationEffect,
  HeroMedia,
  ImageRef,
  StoreConfig,
} from "./types"

export const HERO_ANIMATION_EFFECTS = [
  "none",
  "fade",
  "slide",
  "kenBurns",
] as const satisfies readonly HeroAnimationEffect[]

export const DEFAULT_HERO_ANIMATION_EFFECT: HeroAnimationEffect = "fade"
export const DEFAULT_HERO_INTERVAL_MS = 6000
export const MIN_HERO_INTERVAL_MS = 2000
export const MAX_HERO_INTERVAL_MS = 30000

export interface ResolvedHero {
  /** Section background slides, in display order. Empty ⇒ gradient only. */
  backgrounds: HeroMedia[]
  /** Decorative card slides, in display order. Empty ⇒ no card. */
  cards: HeroMedia[]
  animation: { effect: HeroAnimationEffect; intervalMs: number }
}

/**
 * Narrows the hero fields of a raw StoreConfig to safe, complete values:
 *   - Each photo slot prefers its array (`heroImages` / `heroCardImages`), then
 *     the deprecated single field, then nothing. Entries without a `url` are
 *     dropped so a half-filled admin row never renders a broken image.
 *   - An unknown `effect` falls back to `fade`; `intervalMs` is clamped so a
 *     bad value can neither strobe nor freeze the slideshow.
 */
export function resolveHero(
  config: Pick<
    StoreConfig,
    | "heroImages"
    | "heroCardImages"
    | "heroImage"
    | "heroCardImage"
    | "heroAnimation"
  >
): ResolvedHero {
  const effect = config.heroAnimation?.effect
  const intervalMs = config.heroAnimation?.intervalMs

  return {
    backgrounds: imageSlot(config.heroImages, config.heroImage),
    cards: imageSlot(config.heroCardImages, config.heroCardImage),
    animation: {
      effect:
        effect && (HERO_ANIMATION_EFFECTS as readonly string[]).includes(effect)
          ? effect
          : DEFAULT_HERO_ANIMATION_EFFECT,
      intervalMs:
        typeof intervalMs === "number" && Number.isFinite(intervalMs)
          ? Math.min(
              MAX_HERO_INTERVAL_MS,
              Math.max(MIN_HERO_INTERVAL_MS, intervalMs)
            )
          : DEFAULT_HERO_INTERVAL_MS,
    },
  }
}

/** True for a video slide; anything else (incl. absent `type`) is a photo. */
export function isHeroVideo(media: HeroMedia): boolean {
  return media.type === "video"
}

/**
 * The still image for a slide — the photo itself, or a video's poster — for
 * surfaces that never play video (e.g. the `classic` backdrop). `undefined`
 * for a video without a poster.
 */
export function heroStillImage(media: HeroMedia): ImageRef | undefined {
  if (!isHeroVideo(media)) return media
  return media.posterUrl ? { url: media.posterUrl, alt: media.alt } : undefined
}

function imageSlot(
  images: HeroMedia[] | undefined,
  legacy: ImageRef | undefined
): HeroMedia[] {
  const valid = (images ?? []).filter((image) => Boolean(image?.url))
  if (valid.length > 0) return valid
  return legacy?.url ? [legacy] : []
}
